import React, { useEffect, useState } from 'react';
import { Alert, Checkbox, Form, Input, List, Select, Space, Tag, message } from 'antd';
import UserManagementService from '../../../services/UserManagementService';
import { HCW_POSITIONS } from '../../../constants/hcwPositions';
import { ActionButton, ResponsiveModal } from '../styled';

const splitFullName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] };
};

// Turns an invite result ({ email, sms }) into a short admin-facing summary.
const describeInvite = (invite) => {
  if (!invite) return null;
  const parts = [];
  if (invite.email) parts.push(invite.email.success ? 'email sent' : `email failed (${invite.email.error})`);
  if (invite.sms) parts.push(invite.sms.success ? 'text sent' : `text failed (${invite.sms.error})`);
  return parts.join(', ');
};

const inviteFailed = (invite) =>
  !!invite && [invite.email, invite.sms].some((channel) => channel && !channel.success);

const OUTCOME_TAGS = {
  created: { color: 'green', label: 'Enabled' },
  linked: { color: 'green', label: 'Linked existing profile' },
  reactivated: { color: 'green', label: 'Reactivated' },
  already_enabled: { color: 'default', label: 'Already enabled' },
  skipped: { color: 'orange', label: 'Skipped' }
};

/**
 * Gives one or more portal users access to the Deployed Hearts app.
 * One user: shows the worker-profile details form. Several: enables them
 * with the details already on their accounts and lists anyone skipped.
 */
const HcwAccessModal = ({ open, users = [], onClose, onDone }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const isBulk = users.length > 1;
  const single = users.length === 1 ? users[0] : null;
  const selectedRole = Form.useWatch('professionalRole', form);

  useEffect(() => {
    if (!open) return;
    setBulkResults(null);
    form.resetFields();
    if (single) {
      form.setFieldsValue({
        ...splitFullName(single.fullName),
        phone: single.phone || '',
        professionalRole: single.role === 'volunteer' ? 'Volunteer' : undefined,
        sendEmail: true,
        sendSms: true
      });
    } else {
      form.setFieldsValue({ sendEmail: true, sendSms: true });
    }
  }, [open, single, form]);

  const handleSingle = async (values) => {
    const response = await UserManagementService.setHcwAccess(single._id, { enabled: true, ...values });
    const inviteSummary = describeInvite(response.invite);
    const text = inviteSummary ? `${response.message} — ${inviteSummary}` : response.message;
    if (inviteFailed(response.invite)) message.warning(text, 6);
    else message.success(text);
    onDone();
  };

  const handleBulk = async (values) => {
    const response = await UserManagementService.bulkEnableHcwAccess(
      users.map((u) => u._id),
      values
    );
    setBulkResults(response);
    const { enabled, alreadyEnabled, skipped } = response.summary;
    message.info(`${enabled} enabled, ${alreadyEnabled} already had access, ${skipped} skipped`);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      if (isBulk) await handleBulk(values);
      else await handleSingle(values);
    } catch (error) {
      message.error(error.message || 'Failed to turn on Deployed Hearts access');
    } finally {
      setSubmitting(false);
    }
  };

  const closeBulkResults = () => {
    setBulkResults(null);
    onDone();
  };

  const inviteFields = (
    <Form.Item label="Send invite" style={{ marginBottom: 8 }}>
      <Space direction="vertical" size={4}>
        <Form.Item name="sendEmail" valuePropName="checked" noStyle>
          <Checkbox>Email with download link and sign-in instructions</Checkbox>
        </Form.Item>
        <Form.Item name="sendSms" valuePropName="checked" noStyle>
          <Checkbox>Text message (requires a phone number)</Checkbox>
        </Form.Item>
      </Space>
    </Form.Item>
  );

  if (bulkResults) {
    return (
      <ResponsiveModal
        title="Deployed Hearts access"
        open={open}
        onCancel={closeBulkResults}
        footer={<ActionButton type="primary" onClick={closeBulkResults}>Done</ActionButton>}
        width={560}
      >
        <List
          size="small"
          dataSource={bulkResults.results}
          renderItem={(item) => {
            const tag = OUTCOME_TAGS[item.outcome] || OUTCOME_TAGS.skipped;
            return (
              <List.Item>
                <List.Item.Meta
                  title={<Space>{item.name || item.email}<Tag color={tag.color}>{tag.label}</Tag></Space>}
                  description={item.reason || describeInvite(item.invite) || item.email}
                />
              </List.Item>
            );
          }}
        />
        {bulkResults.summary.skipped > 0 && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Skipped users are usually missing a phone number or last name. Turn on their access individually to fill those in."
          />
        )}
      </ResponsiveModal>
    );
  }

  return (
    <ResponsiveModal
      title={isBulk ? `Give ${users.length} users Deployed Hearts access` : 'Give Deployed Hearts access'}
      open={open}
      onCancel={onClose}
      width={520}
      footer={
        <Space>
          <ActionButton onClick={onClose}>Cancel</ActionButton>
          <ActionButton type="primary" loading={submitting} onClick={handleSubmit}>
            Turn on access
          </ActionButton>
        </Space>
      }
    >
      <p style={{ color: '#595959' }}>
        {isBulk
          ? 'Each user gets a healthcare worker profile using the name and phone number on their account. They sign in to the app with their existing portal email and password.'
          : `${single?.fullName || single?.email} will sign in to the Deployed Hearts app with ${single?.email} and their existing portal password.`}
        {' '}They start as <strong>pending</strong> until their documents are approved on the Healthcare Workers page.
      </p>
      <Form form={form} layout="vertical" requiredMark="optional">
        {!isBulk && (
          <>
            <Space.Compact block>
              <Form.Item
                name="firstName"
                label="First name"
                style={{ width: '50%' }}
                rules={[{ required: true, whitespace: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="lastName"
                label="Last name"
                style={{ width: '50%' }}
                rules={[{ required: true, whitespace: true, message: 'Required' }]}
              >
                <Input />
              </Form.Item>
            </Space.Compact>
            <Form.Item
              name="phone"
              label="Phone"
              rules={[
                { required: true, message: 'A phone number is required for healthcare workers' },
                {
                  validator: (_, value) =>
                    !value || String(value).replace(/\D/g, '').length >= 10
                      ? Promise.resolve()
                      : Promise.reject(new Error('Enter a 10-digit phone number'))
                }
              ]}
            >
              <Input placeholder="(555) 555-5555" />
            </Form.Item>
            <Form.Item name="professionalRole" label="Professional role">
              <Select
                allowClear
                placeholder="Select a role"
                options={HCW_POSITIONS.map((role) => ({ value: role, label: role }))}
              />
            </Form.Item>
            {selectedRole === 'Other' && (
              <Form.Item
                name="professionalRoleOther"
                label="Describe the role"
                rules={[{ required: true, whitespace: true, message: 'Required when role is Other' }]}
              >
                <Input maxLength={80} />
              </Form.Item>
            )}
          </>
        )}
        {inviteFields}
      </Form>
    </ResponsiveModal>
  );
};

export default HcwAccessModal;
